'use client';

import { useQueryClient } from '@tanstack/react-query';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@zond/ui';
import { ChevronsUpDown, Plus } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CreateWorkspaceModal } from '../../../modules/workspace/create-workspace-modal';
import { useStore } from '../../../store';

export function TeamSwitcher() {
  const { t } = useTranslation('navigation');
  const { isMobile } = useSidebar();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isCreateModalOpen, setIsCreateModalOpen] = React.useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);

  // Get all data from store
  const currentWorkspace = useStore((state) => state.getCurrentWorkspace());
  const selectedWorkspaceId = useStore((state) => state.getSelectedWorkspaceId());
  const workspaces = useStore((state) => state.getAllWorkspaces());

  // Handle workspace switching
  const handleWorkspaceSwitch = (workspaceId: number) => {
    // Invalidate all queries before switching workspace
    queryClient.removeQueries();
    navigate(`/${workspaceId}`);
  };

  // Handler for opening modal window
  const handleOpenCreateModal = () => {
    // Close dropdown before opening modal window
    setIsDropdownOpen(false);
    // Open modal window
    setIsCreateModalOpen(true);
  };

  // Handler for closing modal window
  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  if (!workspaces.length || !currentWorkspace) {
    return null;
  }

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                  {/* Display first letter of workspace name as icon */}
                  {currentWorkspace.name?.[0]?.toUpperCase() || 'W'}
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">
                    {currentWorkspace.name || t('teamSwitcher.workspaceFallback')}
                  </span>
                  <span className="truncate text-xs">{t('teamSwitcher.subtitle')}</span>
                </div>
                <ChevronsUpDown className="ml-auto" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
              align="start"
              side={isMobile ? 'bottom' : 'right'}
              sideOffset={4}
            >
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                {t('teamSwitcher.workspaces')}
              </DropdownMenuLabel>
              {workspaces.map((workspace) => (
                <DropdownMenuItem
                  key={workspace.id}
                  onClick={() => handleWorkspaceSwitch(workspace.id)}
                  className="gap-2 p-2"
                >
                  <div className="flex size-6 items-center justify-center rounded-sm border">
                    {(workspace.name || 'W')[0]?.toUpperCase()}
                  </div>
                  {workspace.name || t('teamSwitcher.workspaceFallback')}
                  {workspace.id === selectedWorkspaceId && (
                    <DropdownMenuShortcut>{t('teamSwitcher.active')}</DropdownMenuShortcut>
                  )}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem className="gap-2 p-2" onClick={handleOpenCreateModal}>
                <div className="flex size-6 items-center justify-center rounded-md border bg-background">
                  <Plus className="size-4" />
                </div>
                <div className="font-medium text-muted-foreground">{t('teamSwitcher.createWorkspace')}</div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>

      <CreateWorkspaceModal isOpen={isCreateModalOpen} onClose={handleCloseCreateModal} />
    </>
  );
}
